import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, Mail } from 'lucide-react';
import { consumeSignInCode, requestMagicLink } from '@/api/generated';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Diamond } from '@/components/ui/diamond';
import { Photo } from '@/components/ui/photo';
import { isValidEmail } from '@/lib/booking';
import { captureMagicLinkRequested, captureSignInCodeUsed } from '@/lib/analytics';
import { applySeo } from '@/lib/seo';
import { HERO_PHOTO_URL } from '@/lib/heroPhoto';
import { m } from '@/paraglide/messages';

// Login page (/login).
// Email-only magic-link form. The backend ALWAYS returns 200 (anti-enumeration),
// so we show a neutral confirmation state on success.
//
// The same email also carries a six-digit code, and this page accepts it
// (OH-149). That is not a convenience: a home-screen web app keeps its own
// cookie jar, so following the link signs the guest in INSIDE THEIR BROWSER
// and the installed app they open next is still logged out — with no gesture
// that fixes it, because a web app has no URL scheme to claim the link with.
// Typing the code here, inside the app, is the only way a session lands where
// the app can read it.
//
// Search params:
//   ?error=invalid  — the magic redirect failure landing (expired/invalid link).
//   The backend 302-redirects to /login?error=invalid when the token is bad.
export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { error?: 'invalid' } => ({
    error: s.error === 'invalid' ? 'invalid' : undefined,
  }),
  staticData: { chrome: false },
  component: LoginPage,
});

type Phase = 'idle' | 'submitting' | 'sent' | 'transport_error';

// 'link' asks us to email a sign-in link; 'code' signs in with one already in
// hand. Two ways into the same session, not two accounts.
type Mode = 'link' | 'code';

export function LoginPage() {
  // strict:false so the exported component can also render outside its own route
  // match (unit tests); at /login it returns the validated { error } search.
  const { error: errorParam } = useSearch({ strict: false }) as { error?: 'invalid' };

  // noindex: login page must not be indexed. Also Disallowed in robots.txt.
  useEffect(() => {
    return applySeo({ title: 'Sign in — ollio', noindex: true });
  }, []);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>('idle');
  const [mode, setMode] = useState<Mode>('link');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeSubmitting, setCodeSubmitting] = useState(false);

  const isDisabled = phase === 'submitting' || phase === 'sent';

  function handleReset() {
    setPhase('idle');
    setMode('link');
    setEmail('');
    setEmailError(null);
    setCode('');
    setCodeError(null);
  }

  // handleCodeSubmit is the app's way in. A wrong code and an expired one are
  // one message on purpose — the server refuses to tell them apart, so that
  // this page cannot be used to ask whether an address has an account.
  async function handleCodeSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (code.replace(/\D/g, '').length !== 6) {
      setCodeError('Enter the six-digit code from the email.');
      return;
    }
    setEmailError(null);
    setCodeError(null);
    setCodeSubmitting(true);

    try {
      const { response } = await consumeSignInCode({
        body: { email: email.trim(), code },
      });
      if (response?.status === 204) {
        captureSignInCodeUsed();
        // The cabinet is usually what sent this guest here, so its profile
        // query is already cached as null — and it redirects to /login on a
        // null profile. Without this the new session would bounce straight
        // back to this page. Removing rather than invalidating also keeps one
        // guest's bookings off the next guest's screen on a shared device.
        queryClient.removeQueries({ queryKey: ['guest-profile'] });
        queryClient.removeQueries({ queryKey: ['my-bookings'] });
        void navigate({ to: '/cabinet', replace: true });
        return;
      }
      setCodeError(
        response?.status === 401
          ? 'That code is not right, or it has expired. Request a new link below.'
          : 'Something went wrong — please try again.',
      );
    } catch {
      setCodeError('Something went wrong — please try again.');
    } finally {
      setCodeSubmitting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // Client-side email format guard (server re-validates).
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError(null);
    setPhase('submitting');

    try {
      const { response } = await requestMagicLink({ body: { email: email.trim() } });
      // The backend returns 200 regardless of whether the email is known
      // (anti-enumeration). Any non-5xx response is a success for our purposes.
      if (response && response.status < 500) {
        captureMagicLinkRequested();
        setPhase('sent');
      } else {
        setPhase('transport_error');
      }
    } catch {
      setPhase('transport_error');
    }
  }

  return (
    <div className="flex min-h-full">
      {/* Left: form */}
      <main className="flex flex-1 items-center justify-center px-6 py-12 md:px-12">
        <div className="w-full max-w-sm">
          <Link to="/" aria-label="Ollio — home" className="mb-10 inline-flex items-center gap-2.5">
            <img src="/ollio-monogram-navy.png" alt="" className="h-9 w-auto" />
            <span className="font-serif text-2xl font-semibold tracking-[0.04em] text-[var(--ollio-navy)]">
              OLLIO
            </span>
          </Link>

          {phase === 'sent' ? (
            <SentState
              email={email}
              onReset={handleReset}
              code={code}
              onCodeChange={setCode}
              codeError={codeError}
              codeSubmitting={codeSubmitting}
              onCodeSubmit={handleCodeSubmit}
            />
          ) : (
            <>
              <Eyebrow className="mb-2.5">Welcome Back</Eyebrow>
              <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-[var(--ollio-ink)]">
                Sign in to your cabinet
              </h1>
              <p className="mt-2 text-muted">
                Your reservations and itineraries, all in one place. We'll email you a secure
                sign-in link — no password to remember.
              </p>

              {/* ?error=invalid notice — backend redirected here after a bad/expired link. */}
              {errorParam === 'invalid' && (
                <div
                  role="alert"
                  className="mt-5 rounded-[var(--radius-control)] border border-[var(--ollio-gold)] bg-[rgba(197,165,90,0.1)] px-4 py-3 text-sm text-[var(--ollio-gold-deep)]"
                >
                  That sign-in link was invalid or expired — request a new one.
                </div>
              )}

              <form
                onSubmit={(e) =>
                  mode === 'code' ? void handleCodeSubmit(e) : void handleSubmit(e)
                }
                noValidate
                className="mt-6 space-y-4"
              >
                <Input
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isDisabled}
                  placeholder="you@example.com"
                  leadingIcon={<Mail />}
                  error={emailError ?? undefined}
                />

                {mode === 'code' && (
                  <CodeInput
                    value={code}
                    onChange={setCode}
                    error={codeError}
                    disabled={codeSubmitting}
                  />
                )}

                {phase === 'transport_error' && (
                  <p role="alert" className="text-sm text-destructive">
                    Something went wrong — please try again.
                  </p>
                )}

                <Button
                  type="submit"
                  variant="gold"
                  size="lg"
                  pill
                  className="w-full"
                  disabled={isDisabled || codeSubmitting}
                  loading={phase === 'submitting' || codeSubmitting}
                >
                  {mode === 'code'
                    ? codeSubmitting
                      ? 'Signing in…'
                      : 'Sign in'
                    : phase === 'submitting'
                      ? 'Sending…'
                      : 'Send sign-in link'}
                </Button>

                <ModeSwitch
                  mode={mode}
                  onSwitch={(next) => {
                    setMode(next);
                    setCodeError(null);
                  }}
                />
              </form>
            </>
          )}
        </div>
      </main>

      {/* Right: decorative brand panel (hidden on mobile) */}
      <aside aria-hidden="true" className="relative hidden flex-1 overflow-hidden md:block">
        {/* Same photograph as the home hero (lib/heroPhoto.ts). Decorative
            and hidden below md, so no `priority`: it must not compete with the
            form for bandwidth on the page where someone is trying to sign in. */}
        <Photo src={HERO_PHOTO_URL} sizes="50vw" className="absolute inset-0" />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(15,23,42,.25), rgba(21,45,69,.82))' }}
        />
        <div className="absolute inset-x-0 bottom-0 p-12 text-white lg:p-14">
          <Diamond className="mb-4" />
          <h2 className="max-w-md font-serif text-3xl font-semibold leading-[1.12] lg:text-4xl">
            {m.login_panel_heading()}
          </h2>
          {/* Was "Fifty design-forward homes, one local team, and the best rate
              when you book direct." The fifty was a build estimate that leaked
              into copy (docs/27 Часть 0 п.9), and "the best rate when you book
              direct" is the price comparison with the OTAs that п.1 rules out. */}
          <p className="mt-3 max-w-md text-base leading-relaxed text-white/80">
            {m.login_panel_body()}
          </p>
        </div>
      </aside>
    </div>
  );
}

// CodeInput is the six-digit code from the sign-in email.
//
// inputMode="numeric" so phones open the number pad; autoComplete="one-time-code"
// so iOS offers the code straight from the notification, which on the device
// this feature exists for turns retyping into one tap. Spacing the guest types
// or pastes is kept as typed and normalised server-side — silently rewriting
// the field under the cursor is worse than accepting what they wrote.
function CodeInput({
  value,
  onChange,
  error,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  disabled: boolean;
}) {
  return (
    <Input
      label="Code from the email"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder="123456"
      leadingIcon={<KeyRound />}
      error={error ?? undefined}
    />
  );
}

// ModeSwitch is how a guest reaches the code path when they did not start
// here — they requested the link in their browser, then opened the app.
function ModeSwitch({ mode, onSwitch }: { mode: Mode; onSwitch: (next: Mode) => void }) {
  const isCode = mode === 'code';
  return (
    <button
      type="button"
      onClick={() => onSwitch(isCode ? 'link' : 'code')}
      className="w-full text-center text-sm font-semibold text-[var(--ollio-navy)] underline-offset-2 hover:underline"
    >
      {isCode ? 'Email me a sign-in link instead' : 'I already have a code'}
    </button>
  );
}

function SentState({
  email,
  onReset,
  code,
  onCodeChange,
  codeError,
  codeSubmitting,
  onCodeSubmit,
}: {
  email: string;
  onReset: () => void;
  code: string;
  onCodeChange: (v: string) => void;
  codeError: string | null;
  codeSubmitting: boolean;
  onCodeSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <div>
      <Eyebrow className="mb-2.5">Almost there</Eyebrow>
      <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-[var(--ollio-ink)]">
        Check your email
      </h1>
      <p className="mt-2 text-muted">
        If an account is associated with{' '}
        <span className="font-semibold text-[var(--ollio-ink)]">{email}</span>, we've sent a
        sign-in link. It expires in 15 minutes.
      </p>

      {/* The code, offered right here rather than behind a second click. A
          guest reading this inside the installed app cannot use the link at
          all — it would open their browser and sign them in over there. */}
      <form onSubmit={onCodeSubmit} noValidate className="mt-6 space-y-4">
        <p className="text-sm text-muted">
          The email also has a six-digit code. Enter it here to sign in on this device.
        </p>
        <CodeInput
          value={code}
          onChange={onCodeChange}
          error={codeError}
          disabled={codeSubmitting}
        />
        <Button
          type="submit"
          variant="gold"
          size="lg"
          pill
          className="w-full"
          disabled={codeSubmitting}
          loading={codeSubmitting}
        >
          {codeSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <button
        type="button"
        onClick={onReset}
        className="mt-6 text-sm font-semibold text-[var(--ollio-navy)] underline-offset-2 hover:underline"
      >
        Use a different email
      </button>
    </div>
  );
}
